import { useRef, useState, type ChangeEvent } from 'react';
import { api } from '@aeon-karaoke/cloud-convex';
import { TRACK_STATE_LABEL, trackState } from '@aeon-karaoke/cloud-convex/model/trackState';
import { Button, Input, Modal, Toggle } from '@/component';
import { useTrackUpload, type TrackView } from '@/hook';
import { AUDIO_ACCEPT, inspectAudioFile } from '@/lib/audioFile';
import { convex } from '@/lib/convex';
import { toResult } from '@/lib/result-adapter';

interface ManageTrackModalProps {
  track: TrackView | null;
  onClose: () => void;
}

const KICKER_TONE = { ready: 'up', preparing: 'acc', attention: 'down' } as const;

/** Manage modal from the manager rows: rename, the session-queue toggle,
 * Replace audio, Download and Delete. Remounts per track so field state
 * never leaks between rows. */
export function ManageTrackModal({ track, onClose }: ManageTrackModalProps) {
  if (!track) return null;
  return <ManageTrackForm key={track._id} track={track} onClose={onClose} />;
}

function ManageTrackForm({ track, onClose }: { track: TrackView; onClose: () => void }) {
  const [title, setTitle] = useState(track.title);
  const [artist, setArtist] = useState(track.artist ?? '');
  const [share, setShare] = useState(track.allowSessionQueue ?? false);
  const persistedShare = useRef(track.allowSessionQueue ?? false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const { upload } = useTrackUpload();
  const state = trackState(track.processingStatus);

  async function run<T>(call: () => Promise<T>) {
    setBusy(true);
    setError(null);
    const result = await toResult(call);
    setBusy(false);
    if (!result.ok) setError(result.error.message);
    return result;
  }

  async function save() {
    const result = await run(() =>
      convex.mutation(api.tracks.updateMine, { trackId: track._id, title, artist: artist.trim() || null })
    );
    if (result.ok) onClose();
  }

  async function toggleShare(next: boolean) {
    setShare(next);
    const result = await run(() =>
      convex.mutation(api.tracks.setAllowSessionQueue, { trackId: track._id, allow: next })
    );
    if (result.ok) persistedShare.current = next;
    else setShare(persistedShare.current);
  }

  /** The upload contract from an existing track: replaceSource issues the
   * intent, the PUT lands, submit swaps the source and rebuilds. */
  async function replaceAudio(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const check = await inspectAudioFile(file);
    if (!check.ok) return setError(check.message);
    const intent = await run(() =>
      convex.mutation(api.tracks.replaceSource, {
        trackId: track._id,
        fileName: file.name,
        bytes: file.size,
        contentType: file.type || undefined,
        durationMs: check.durationMs,
      })
    );
    if (!intent.ok) return;
    setBusy(true);
    const uploaded = await upload(file, intent.value);
    if (!uploaded.ok) {
      setBusy(false);
      return setError(uploaded.error.message);
    }
    const result = await run(() =>
      convex.mutation(api.tracks.submit, {
        trackId: track._id,
        title: title.trim() || track.title,
        artist: artist.trim() || null,
        allowSessionQueue: share,
      })
    );
    if (result.ok) onClose();
  }

  /** The tab opens inside the click so the browser keeps the user activation;
   * the signed URL only arrives after an await. */
  async function download() {
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    const got = await run(() => convex.query(api.tracks.getDownloadUrl, { trackId: track._id }));
    if (got.ok && tab) tab.location.href = got.value.url;
    else tab?.close();
  }

  async function remove() {
    const result = await run(() => convex.mutation(api.tracks.remove, { trackId: track._id }));
    if (result.ok) onClose();
  }

  return (
    <Modal open onClose={onClose} title="Manage track" kicker={TRACK_STATE_LABEL[state]} kickerTone={KICKER_TONE[state]} maxWidth={520}>
      <div className="mt-[26px] flex flex-col gap-[18px]">
        <Input label="Song title" value={title} onChange={(event) => setTitle(event.target.value)} className="bg-bg" />
        <Input label="Artist" value={artist} onChange={(event) => setArtist(event.target.value)} className="bg-bg" />
        <div className="flex items-start justify-between gap-5 pt-[6px]">
          <div>
            <div className="text-[15px] font-semibold">Friends can queue it</div>
            <p className="mt-1 max-w-[36ch] text-sm leading-[1.5] text-mute">Anyone in a session with you can add it.</p>
          </div>
          <Toggle checked={share} onChange={(next) => void toggleShare(next)} label="Friends can queue it" />
        </div>
      </div>
      <div className="mt-7 flex flex-wrap gap-[10px]">
        <Button onClick={() => void save()} disabled={busy || !title.trim()} className="px-[18px]">
          Save changes
        </Button>
        <Button variant="outline" disabled={busy} onClick={() => fileInput.current?.click()} className="px-[17px]">
          Replace audio
        </Button>
        <Button
          variant="outline"
          quiet
          disabled={busy || state !== 'ready'}
          onClick={() => void download()}
          className="px-[17px]"
        >
          Download
        </Button>
        <input ref={fileInput} type="file" accept={AUDIO_ACCEPT} onChange={(event) => void replaceAudio(event)} className="hidden" />
      </div>
      {error && <p className="mt-4 text-[13px] text-down">{error}</p>}
      <div className="mt-6 border-t border-line pt-[18px]">
        {confirmDelete ? (
          <div className="flex items-center gap-4">
            <button type="button" onClick={() => void remove()} disabled={busy} className="text-[13px] font-semibold text-down">
              Delete for good
            </button>
            <button type="button" onClick={() => setConfirmDelete(false)} className="text-[13px] text-mute">
              Keep it
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => setConfirmDelete(true)} className="text-[13px] font-semibold text-down">
            Delete this track
          </button>
        )}
        <p className="mt-[6px] max-w-[46ch] text-xs leading-[1.6] text-faint">
          Deleting removes it from your library and from anyone who queued it. This cannot be undone.
        </p>
      </div>
    </Modal>
  );
}
